import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Body from "src/components/common/Body";
import Button from "src/components/common/Button";
import IconButton from "src/components/common/IconButton";
import AlarmButton from "src/components/common/AlarmButton";
import SVG from "src/utils/SVG";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "src/store/actions/action";
import cn from "classnames";
import { format } from "date-fns";

// 데이터 타입
interface SettlementItem {
  id: number;
  brand: string;
  companyName: string;
  repairContent: string; // cate_name
  storeName: string;
  storeType: string;
  requestDate: string;
  repairSchedule: string;
  hasEstimate: boolean;
  hasTaxInvoice: boolean;
  status: string; // 완료/미완료
  amount: number;
}

// 임시 데이터
const mockData: SettlementItem[] = [
  {
    id: 1,
    brand: "MLB",
    companyName: "㈜건설",
    repairContent: "천장 누수 보수",
    storeName: "강남점",
    storeType: "백화점",
    requestDate: "2024-11-20",
    repairSchedule: "2024-11-24",
    hasEstimate: true,
    hasTaxInvoice: true,
    status: "완료",
    amount: 1500000,
  },
  {
    id: 2,
    brand: "MLB KIDS",
    companyName: "㈜인테리어",
    repairContent: "바닥 타일 교체",
    storeName: "홍대점",
    storeType: "대리점",
    requestDate: "2024-11-19",
    repairSchedule: "2024-11-23",
    hasEstimate: true,
    hasTaxInvoice: false,
    status: "미완료",
    amount: 2300000,
  },
  {
    id: 3,
    brand: "Discovery",
    companyName: "㈜전기",
    repairContent: "조명 교체",
    storeName: "명동점",
    storeType: "직영점",
    requestDate: "2024-11-18",
    repairSchedule: "2024-11-22",
    hasEstimate: true,
    hasTaxInvoice: true,
    status: "완료",
    amount: 800000,
  },
];

// Styled Components - 컴포넌트보다 먼저 정의
const Container = styled.div`
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FilterSection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const MonthSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  label {
    font-weight: 600;
    color: #495057;
  }

  input {
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 14px;
  }
`;

const FilterSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  label {
    font-weight: 600;
    color: #495057;
  }

  select {
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 14px;
    min-width: 150px;
  }
`;

const SortSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  label {
    font-weight: 600;
    color: #495057;
  }

  select {
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 14px;
    min-width: 150px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;

    button {
      flex: 1;
    }
  }
`;

const TableSection = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #dee2e6;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #212529;
  }

  > div {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;

    > div {
      width: 100%;
      justify-content: space-between;
    }
  }
`;

const TotalAmount = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #212529;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8f9fa;

    th {
      padding: 12px;
      text-align: left;
      font-weight: 600;
      font-size: 14px;
      color: #495057;
      border-bottom: 2px solid #dee2e6;
      white-space: nowrap;

      &:first-child {
        width: 40px;
        text-align: center;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #dee2e6;
      transition: background 0.2s;

      &:hover {
        background: #f8f9fa;
      }
    }

    td {
      padding: 12px;
      font-size: 14px;
      color: #212529;

      &:first-child {
        text-align: center;
      }

      &.center {
        text-align: center;
      }
    }
  }

  tfoot {
    background: #f8f9fa;
    font-weight: 600;

    td {
      padding: 12px;
      font-size: 14px;
      color: #212529;
      border-top: 2px solid #dee2e6;
    }
  }
`;

const CheckIcon = styled.span`
  color: #28a745;
  font-weight: bold;
  font-size: 16px;
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 8px;
  background: ${({ color }) => color};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

// Mobile Styles
const MobileCardList = styled.div`
  padding: 16px;
`;

const MobileCard = styled.div`
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
  }
`;

const StoreName = styled.div`
  flex: 1;
  font-weight: 600;
  font-size: 15px;
  color: #212529;
`;

const CardBody = styled.div`
  padding: 12px;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;

  &:not(:last-child) {
    border-bottom: 1px solid #f1f3f5;
  }
`;

const CardLabel = styled.span`
  color: #6c757d;
  font-weight: 500;
`;

const CardValue = styled.span`
  color: #212529;
  text-align: right;
`;

const MonthlySettlement = () => {
  const navigate = useNavigate();
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);
  const [hasAlarm, setHasAlarm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [selectedBrand, setSelectedBrand] = useState("");
  const [sortBy, setSortBy] = useState("brand"); // brand, company, requestDate

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 필터링 및 정렬 함수
  const filterAndSortData = (data: SettlementItem[]) => {
    // 브랜드 필터링
    let filtered = [...data];
    if (selectedBrand) {
      filtered = filtered.filter(item => {
        // 브랜드 코드를 실제 브랜드명으로 매칭
        const brandMap: { [key: string]: string } = {
          "M": "MLB",
          "K": "MLB KIDS",
          "D": "Discovery",
          "V": "DUVETICA"
        };
        return item.brand === brandMap[selectedBrand];
      });
    }

    // 정렬
    switch (sortBy) {
      case "brand":
        return filtered.sort((a, b) => a.brand.localeCompare(b.brand));
      case "company":
        return filtered.sort((a, b) => a.companyName.localeCompare(b.companyName));
      case "requestDate":
        return filtered.sort((a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime());
      default:
        return filtered;
    }
  };

  const sortedData = filterAndSortData(mockData);

  // 전체 선택/해제
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedItems(sortedData.map((item) => String(item.id)));
    } else {
      setSelectedItems([]);
    }
  };

  // 개별 선택
  const handleSelectItem = (id: number) => {
    const idStr = String(id);
    if (selectedItems.includes(idStr)) {
      setSelectedItems(selectedItems.filter((item) => item !== idStr));
    } else {
      setSelectedItems([...selectedItems, idStr]);
    }
  };

  // 금액 포맷
  const formatAmount = (amount: number) => {
    return amount.toLocaleString("ko-KR");
  };

  // 상태에 따른 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case "완료":
        return "#28a745";
      case "미완료":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  return (
    <>
      <header className={cn({ hasAlarm: hasAlarm })}>
        <div>
          <Button variant="text" className="btn-back no-ico">
            월별 정산 리스트
          </Button>
        </div>
        <div className="btn-box">
          <AlarmButton />
          <IconButton
            btnName="마이페이지"
            className="user"
            onClick={() => navigate("/profile/" + userInfo.user_id)}
          >
            <SVG name="userBlack" />
          </IconButton>
        </div>
      </header>

      <Body isTabbar={true}>
        <Container>
          <FilterSection>
            <FilterGroup>
              <MonthSelector>
                <label>정산월:</label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
              </MonthSelector>
              <FilterSelector>
                <label>브랜드:</label>
                <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)}>
                  <option value="">전체 브랜드</option>
                  <option value="M">MLB</option>
                  <option value="K">MLB KIDS</option>
                  <option value="D">Discovery</option>
                  <option value="V">DUVETICA</option>
                </select>
              </FilterSelector>
              <SortSelector>
                <label>정렬:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="brand">브랜드순</option>
                  <option value="company">업체명순</option>
                  <option value="requestDate">접수일자순</option>
                </select>
              </SortSelector>
            </FilterGroup>
            <ButtonGroup>
              <Button variant="outlined" size="small" onClick={() => {
                setSelectedMonth(format(new Date(), "yyyy-MM"));
                setSelectedBrand("");
                setSortBy("brand");
              }}>
                초기화
              </Button>
              <Button variant="contained" size="small">
                검색
              </Button>
            </ButtonGroup>
          </FilterSection>

          <TableSection>
            <TableHeader>
              <h3>{selectedMonth} 정산 내역 ({sortedData.length}건)</h3>
              <div>
                <TotalAmount>
                  총액: ₩ {formatAmount(sortedData.reduce((sum, item) => sum + item.amount, 0))}
                </TotalAmount>
                <ExportButton>
                  <SVG name="fileDownload" width={16} height={16} />
                  엑셀 다운로드
                </ExportButton>
              </div>
            </TableHeader>

            {isMobile ? (
              <MobileCardList>
                {sortedData.map((item) => (
                  <MobileCard key={item.id}>
                    <CardHeader>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(String(item.id))}
                        onChange={() => handleSelectItem(item.id)}
                      />
                      <StoreName>{item.storeName}</StoreName>
                      <StatusBadge color={getStatusColor(item.status)}>
                        {item.status}
                      </StatusBadge>
                    </CardHeader>
                    <CardBody>
                      <CardRow>
                        <CardLabel>브랜드</CardLabel>
                        <CardValue>{item.brand}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>업체명</CardLabel>
                        <CardValue>{item.companyName}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>보수내역</CardLabel>
                        <CardValue>{item.repairContent}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>매장유형</CardLabel>
                        <CardValue>{item.storeType}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>접수일자</CardLabel>
                        <CardValue>{item.requestDate}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>보수일정</CardLabel>
                        <CardValue>{item.repairSchedule}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>견적서</CardLabel>
                        <CardValue>{item.hasEstimate ? "✓" : "-"}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>세금계산서</CardLabel>
                        <CardValue>{item.hasTaxInvoice ? "✓" : "-"}</CardValue>
                      </CardRow>
                      <CardRow>
                        <CardLabel>금액</CardLabel>
                        <CardValue>₩ {formatAmount(item.amount)}</CardValue>
                      </CardRow>
                    </CardBody>
                  </MobileCard>
                ))}
              </MobileCardList>
            ) : (
              <TableWrapper>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={selectedItems.length === sortedData.length && sortedData.length > 0}
                        />
                      </th>
                      <th>브랜드</th>
                      <th>업체명</th>
                      <th>보수내역</th>
                      <th>매장명</th>
                      <th>매장유형</th>
                      <th>접수일자</th>
                      <th>보수일정</th>
                      <th>견적서</th>
                      <th>세금계산서</th>
                      <th>조치여부</th>
                      <th>금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(String(item.id))}
                            onChange={() => handleSelectItem(item.id)}
                          />
                        </td>
                        <td>{item.brand}</td>
                        <td>{item.companyName}</td>
                        <td>{item.repairContent}</td>
                        <td>{item.storeName}</td>
                        <td>{item.storeType}</td>
                        <td>{item.requestDate}</td>
                        <td>{item.repairSchedule}</td>
                        <td className="center">
                          {item.hasEstimate ? (
                            <CheckIcon>✓</CheckIcon>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="center">
                          {item.hasTaxInvoice ? (
                            <CheckIcon>✓</CheckIcon>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td>
                          <StatusBadge color={getStatusColor(item.status)}>
                            {item.status}
                          </StatusBadge>
                        </td>
                        <td>₩ {formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={11}>합계</td>
                      <td>
                        ₩{" "}
                        {formatAmount(
                          sortedData.reduce((sum, item) => sum + item.amount, 0)
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </StyledTable>
              </TableWrapper>
            )}
          </TableSection>
        </Container>
      </Body>
    </>
  );
};

export default MonthlySettlement;